export type FeedbackTreeQuestionId = 'actionsTaken' | 'stepsOutcome' | 'savedSteps' | 'whatsAvailable';

export type FeedbackResponseQuestionId = 'actionsTaken' | 'stepsOutcome' | 'savedStepsReason' | 'whatsAvailable';

export type FeedbackResponseAnswerId =
  | 'followedSteps'
  | 'savedSteps'
  | 'whatsAvailable'
  | 'somethingElse'
  | 'fixedIt'
  | 'notFixed'
  | 'partlyFixed'
  | 'didNotFinishSteps'
  | 'notAtAppliance'
  | 'neededPart'
  | 'noTime'
  | 'other'
  | 'notSureWhatSmartFixIs'
  | 'wasCurious'
  | 'toKnowIfRepairNeeded'
  | 'fixingMyself'
  | 'fasterRepair';

export interface FeedbackAnswerNode {
  id: FeedbackResponseAnswerId;
  text: string;
  sendValue?: FeedbackResponseAnswerId;
  nextQuestionId: FeedbackTreeQuestionId | null;
  problemIsResolved?: boolean;
}

export interface FeedbackRadioQuestionNode {
  id: string;
  text: string;
  type: 'radio';
  sendAs?: FeedbackResponseQuestionId;
  answers: FeedbackAnswerNode[];
}

export type FeedbackQuestionNode = FeedbackRadioQuestionNode;

export const feedbackQuestionTree: Record<FeedbackTreeQuestionId, FeedbackQuestionNode> = {
  actionsTaken: {
    id: 'actionsTaken',
    text: 'Which of these best describes what you did today?',
    type: 'radio',
    answers: [
      {
        id: 'followedSteps',
        text: 'I followed the steps at my appliance',
        nextQuestionId: 'stepsOutcome',
      },
      {
        id: 'savedSteps',
        text: `I've saved the steps to try later`,
        nextQuestionId: 'savedSteps',
      },
      {
        id: 'whatsAvailable',
        text: `I was having a look at what's available`,
        nextQuestionId: 'whatsAvailable',
      },
      {
        id: 'somethingElse',
        text: `Something else`,
        nextQuestionId: null,
      },
    ],
  },
  stepsOutcome: {
    id: 'stepsOutcome',
    text: 'Did the steps fix the issue?',
    type: 'radio',
    sendAs: 'stepsOutcome',
    answers: [
      {
        id: 'fixedIt',
        text: 'Yes, it fixed it',
        problemIsResolved: true,
        nextQuestionId: null,
      },
      {
        id: 'notFixed',
        text: `No, it didn't fix it`,
        problemIsResolved: false,
        nextQuestionId: null,
      },
      {
        id: 'partlyFixed',
        text: `Partly, some steps worked, some didn’t`,
        problemIsResolved: false,
        nextQuestionId: null,
      },
      {
        id: 'didNotFinishSteps',
        text: `I didn't finish the steps`,
        problemIsResolved: false,
        nextQuestionId: null,
      },
    ],
  },
  savedSteps: {
    id: 'savedSteps',
    text: 'What stopped you doing it today?',
    type: 'radio',
    sendAs: 'savedStepsReason',
    answers: [
      {
        id: 'notAtAppliance',
        text: `I wasn't at my appliance`,
        nextQuestionId: null,
      },
      {
        id: 'neededPart',
        text: 'I need a part or a tool first',
        nextQuestionId: null,
      },
      {
        id: 'noTime',
        text: `I didn't have time today`,
        nextQuestionId: null,
      },
      {
        id: 'other',
        text: 'Something else',
        nextQuestionId: null,
      },
    ],
  },
  whatsAvailable: {
    id: 'whatsAvailable',
    text: 'What interested you in trying it?',
    type: 'radio',
    answers: [
      {
        id: 'notSureWhatSmartFixIs',
        text: 'I didn’t understand what Smart-fix was',
        nextQuestionId: 'stepsOutcome',
      },
      {
        id: 'wasCurious',
        text: 'I was curious how it works',
        nextQuestionId: 'stepsOutcome',
      },
      {
        id: 'toKnowIfRepairNeeded',
        text: 'I wanted to know if I needed a repair',
        nextQuestionId: 'stepsOutcome',
      },
      {
        id: 'fixingMyself',
        text: 'I wanted to fix it myself',
        nextQuestionId: 'stepsOutcome',
      },
      {
        id: 'fasterRepair',
        text: 'I wanted a faster repair',
        nextQuestionId: 'stepsOutcome',
      },
      {
        id: 'other',
        text: 'Something else',
        nextQuestionId: 'stepsOutcome',
      },
    ],
  },
};
